import { Chip, Tooltip } from '@mui/material'
import {
  Datagrid,
  DateField,
  EditButton,
  FunctionField,
  TextField,
  usePermissions,
  useStore,
  useTranslate,
} from 'react-admin'
import AssignVetButton from '../clinic-team/AssignVetButton'
import { useAssigneeNames } from '../clinic-team/useAssigneeNames'
import { petAssigneeIds } from '../clinics/clinicAssociation'
import ToneChip from '../components/ToneChip'
import { usePetLinkedClinics } from '../hooks/usePetLinkedClinics'
import type { Device } from '../types'
import { formatDobForDisplay } from './dob'
import { getLinkedClinicDisplayName, resolveVetClinicName } from './getVetClinicName'
import HidePetButton from './HidePetButton'
import HighlightedText from './HighlightedText'
import { derivePetDataStatus, type PetDataStatus, petDataStatusTone } from './petDataStatus'
import WeightHistoryTooltip from './WeightHistoryTooltip'

interface PetDatagridProps {
  rowClick?: 'edit' | 'show' | 'select' | ((id: any, resource: any, record: any) => any)
  /**
   * Optional device + metrics context for the Data Status column. When not
   * provided (e.g. embedded in places that haven't been wired up yet) the
   * column degrades to no chip rather than rendering a misleading one.
   */
  devicesWithMetrics?: ReadonlySet<number> | null
  allDevices?: ReadonlyArray<Device>
  /**
   * Per-pet unix-SECONDS time of the newest real reading across its devices.
   * Deliberately NOT the device's `start_time`: that is the sensor ACTIVATION
   * time and a LOWER bound on every metric, so comparing it to `now` reported
   * sensor AGE — a healthy 10-day-old sensor read "No Recent Data" while a
   * sensor that died 11h into its life read "Active". A pet absent from this
   * map is UNKNOWN (still loading, or /devices-last-reading failed), never
   * "silent"; the derivation leans active rather than alarming there.
   */
  petLastReading?: ReadonlyMap<number, number>
}

// Responsible Vet cell: every assignee (vic/vetpo#70), comma-separated. A hook
// of its own because names resolve per row (useAssigneeNames).
const AssigneesCell = ({ record }: { record: any }) => {
  const translate = useTranslate()
  const names = useAssigneeNames(record)
  if (petAssigneeIds(record).length === 0) {
    // "Unassigned", not the empty-data dash: this row HAS an answer and the
    // answer is "nobody yet". A dash reads as missing data and is what let an
    // unassigned patient pass for an assigned one.
    return (
      <span style={{ color: 'rgba(0, 0, 0, 0.6)' }}>
        {translate('pos.clinic_team.unassigned_short')}
      </span>
    )
  }
  // An id that resolves to no name shows as `#id` — rendering "Unassigned"
  // there would be an outright false negative.
  return <HighlightedText text={names.join(', ')} />
}

const PetDatagrid = ({
  rowClick = 'edit',
  devicesWithMetrics,
  allDevices,
  petLastReading,
}: PetDatagridProps) => {
  const translate = useTranslate()
  const { permissions } = usePermissions()
  const [selectedPetId, setSelectedPetId] = useStore('selectedPetId')
  const [, setSelectedDeviceId] = useStore('selectedDeviceId')
  // Authoritative pet→clinics map (four-branch SQL); null degrades the Vet
  // Account column to the legacy owner.vets resolution.
  const { clinicsByPetId } = usePetLinkedClinics()

  const handleRowClick =
    typeof rowClick === 'function'
      ? rowClick
      : (id: any, resource: any, record: any) => {
          if (rowClick === 'select') {
            setSelectedDeviceId(undefined)
            setSelectedPetId(record.id)
            return ''
          }
          return rowClick
        }

  const dataStatusLabel = (status: PetDataStatus): string => {
    switch (status) {
      case 'active':
        return translate('resources.pets.data_status.active', {
          _: 'Active',
        })
      case 'waiting_sync':
        return translate('resources.pets.data_status.waiting_sync', {
          _: 'Waiting Sync',
        })
      case 'no_sensor':
        return translate('resources.pets.data_status.no_sensor', {
          _: 'No Sensor',
        })
      case 'data_error':
        return translate('resources.pets.data_status.data_error', {
          _: 'No Recent Data',
        })
      case 'expired':
        return translate('resources.pets.data_status.expired', {
          _: 'Sensor Expired',
        })
      case 'archived':
        return translate('resources.pets.data_status.archived', {
          _: 'Archived',
        })
    }
  }

  const dataStatusTooltip = (status: PetDataStatus): string => {
    switch (status) {
      case 'active':
        return translate('resources.pets.data_status_tooltip.active', {
          _: 'Recent CGM readings received.',
        })
      case 'waiting_sync':
        return translate('resources.pets.data_status_tooltip.waiting_sync', {
          _: 'Sensor paired, waiting for first sync.',
        })
      case 'no_sensor':
        return translate('resources.pets.data_status_tooltip.no_sensor', {
          _: 'No CGM device linked yet — pair a sensor to start collecting data.',
        })
      case 'data_error':
        return translate('resources.pets.data_status_tooltip.data_error', {
          _: 'No readings for over 24h — the sensor may have finished its wear, come off, or lost its phone connection.',
        })
      case 'expired':
        return translate('resources.pets.data_status_tooltip.expired', {
          _: 'This sensor has reached the end of its 14-day wear life. Replace it to resume monitoring.',
        })
      case 'archived':
        return translate('resources.pets.data_status_tooltip.archived', {
          _: 'Pet record archived.',
        })
    }
  }

  const hasDataStatusContext = allDevices !== undefined

  return (
    <Datagrid
      rowClick={handleRowClick}
      bulkActionButtons={false}
      // 13 columns in one card: auto table layout squeezed the name columns
      // until "User 2 Tablet" wrapped one word per line and headers split in
      // two. Name columns keep a readable minimum and headers stay on one
      // line; when that is wider than the card, the table scrolls inside it
      // (theme: RaDatagrid tableWrapper) instead of crushing the text.
      sx={{
        '& .RaDatagrid-headerCell': { whiteSpace: 'nowrap' },
        '& .pet-col-name': { minWidth: 140 },
      }}
      rowStyle={(record) =>
        record.id === selectedPetId ? { backgroundColor: 'rgba(79, 60, 201, 0.08)' } : {}
      }
    >
      <TextField source="id" />
      <FunctionField
        cellClassName="pet-col-name"
        label={translate('resources.pets.fields.owner')}
        render={(record) => (
          <HighlightedText text={record.owner?.name ?? translate('pos.common.empty_data')} />
        )}
      />
      <FunctionField
        cellClassName="pet-col-name"
        source="name"
        label={translate('resources.pets.fields.pet_name')}
        render={(record) => (
          <HighlightedText text={record.name ?? translate('pos.common.empty_data')} />
        )}
      />
      {hasDataStatusContext && (
        <FunctionField
          label={translate('resources.pets.fields.data_status', { _: 'Data Status' })}
          render={(record) => {
            const status = derivePetDataStatus({
              pet: record,
              devices: allDevices ?? [],
              devicesWithMetrics: devicesWithMetrics ?? null,
              latestMetricTime: petLastReading?.get(record.id) ?? null,
            })
            return (
              <Tooltip title={dataStatusTooltip(status)}>
                <ToneChip label={dataStatusLabel(status)} tone={petDataStatusTone(status)} />
              </Tooltip>
            )
          }}
        />
      )}
      <FunctionField
        label={translate('resources.pets.fields.dob')}
        render={(record) => formatDobForDisplay(record.dob) || translate('pos.common.empty_data')}
      />
      <TextField source="species" />
      <TextField source="breed" />
      <FunctionField
        source="weight"
        render={(record) => (
          <WeightHistoryTooltip
            weights={record.profile?.weights || []}
            currentWeight={record.weight}
          >
            <span style={{ cursor: 'help' }}>
              {record.weight || translate('pos.common.empty_data')}
            </span>
          </WeightHistoryTooltip>
        )}
      />
      <FunctionField
        cellClassName="pet-col-name"
        label={translate('resources.pets.fields.vet_account')}
        render={(record) => {
          const empty = translate('pos.common.empty_data')
          const linked = clinicsByPetId?.get(record.id)
          const text = resolveVetClinicName(clinicsByPetId, record, empty)

          if (!record.owner && (!linked || linked.length === 0)) {
            return <span title={translate('resources.pets.fields.vet_account_not_bound')}>—</span>
          }

          // Multiple linked clinics: full list on hover
          if (linked && linked.length > 1) {
            return (
              <Tooltip
                title={linked
                  .map((c) => getLinkedClinicDisplayName(c))
                  .filter(Boolean)
                  .join(', ')}
              >
                <span>
                  <HighlightedText text={text} />
                </span>
              </Tooltip>
            )
          }

          return <HighlightedText text={text} />
        }}
      />
      {/* Clinic Team, PRD §5.4: who is RESPONSIBLE for this patient
          (pets.assigned_vet_id). Phase 2 shipped the Assign action with no
          column reading it back, so an assignment was invisible the moment the
          toast faded — the gap behind Hillside Animal Hospital's 2026-08-03
          report. Deliberately NOT an "Alert Recipient" column: alert routing
          still keys on owner.vets until ALERT_ROUTING_V2 is enabled, so such a
          column would state something false today. */}
      <FunctionField
        label={translate('resources.pets.fields.assigned_vet')}
        render={(record) => <AssigneesCell record={record} />}
      />
      <DateField source="createdAt" label={translate('resources.pets.fields.created')} />
      {/* Clinic Team: master-only per-row "assign vet" action (renders null for
          non-masters). Sets pet.assignedVet → POST /api/pets/:id/assign-vet. */}
      <AssignVetButton />
      {/* vic/vetpo#10: 'vet'-role-only per-row clinic hide (renders null for
          every other role). POSTs /api/pets/:id/clinic-hide. */}
      <HidePetButton />
      {/* Hide edit button for siteadmin (read-only role) */}
      {permissions?.role !== 'siteadmin' && <EditButton />}
    </Datagrid>
  )
}

export default PetDatagrid
